import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

/**
 * OKS-40 — Mobile sub-nav for Brand / Agency / Creator Settings shells.
 *
 * UX Spec §5: collapses the desktop vertical button-list into a
 * single `<Select>` at the top of the page on `< md` widths. Picked
 * over `<Sheet>` for fewer interactions per UX Spec §9.
 *
 * Renders ONLY on `< md` — the parent layout hides it via Tailwind
 * `md:hidden`. Server-driven `currentHref` keeps the active item in
 * sync with the URL across sub-page navigation.
 *
 * Selecting a new item calls `Inertia.router.get` so navigation
 * preserves the SPA shell (no full reload).
 */
export interface SettingsSubNavMobileItem {
  label: string
  href: string
  testId?: string
}

export interface SettingsSubNavMobileProps {
  items: readonly SettingsSubNavMobileItem[]
  currentHref: string
  ariaLabel?: string
  testId?: string
}

export function SettingsSubNavMobile({
  items,
  currentHref,
  ariaLabel,
  testId = "settings-sub-nav-mobile",
}: SettingsSubNavMobileProps) {
  const { t } = useTranslation()

  // Match by exact path OR query-extension prefix so a nested
  // sub-page (?tab=…) still highlights the parent item.
  const activeHref =
    items.find(
      (item) =>
        currentHref === item.href || currentHref.startsWith(`${item.href}?`),
    )?.href ?? items[0]?.href

  return (
    <div data-testid={testId} className="md:hidden">
      <Select
        items={items.map((item) => ({ value: item.href, label: item.label }))}
        value={activeHref}
        onValueChange={(href) => {
          if (href != null && href !== currentHref) router.get(href)
        }}
      >
        <SelectTrigger
          aria-label={ariaLabel ?? t("settings.shared.mobile_nav_label")}
          data-testid={`${testId}-trigger`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem
              key={item.href}
              value={item.href}
              data-testid={item.testId}
            >
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default SettingsSubNavMobile
